import React, { useEffect } from "react";
import Head from "next/head";
import { useRouter } from "next/router";

import { Action, Category, Label } from "constants/events";
import { SEARCH_INITIAL_TAKE } from "constants/search";
import useDomain from "hooks/useDomain";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useSearch from "redux/hooks/useSearch";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import { getRoundedDistance } from "utils/distance";

import ArticleResults from "components/ArticleResults";
import BrandResults from "components/BrandResults";
import DispensaryResults from "components/DispensaryResults";
import MenuItemResults from "components/MenuItemResults";
import SearchAd from "components/SearchAd";
import SearchFilter from "components/SearchFilter";
import SearchFiltersPlaceholder from "components/SearchFilter/SearchFiltersPlaceholder";
import DealsCarousel from "components/Shared/DealsCarousel";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";
import SearchResultsPlaceholder from "components/Shared/Skeletons/SearchResults/SearchResultsPlaceholder";
import StrainResults from "components/StrainResults";
import TrackImpression from "components/TrackImpression";

const SearchPage: React.FC = () => {
  const domainCountryCode = useDomainCountryCode();

  const {
    loading,
    searchTerm,
    categoryRelevanceOrder,
    brand,
    dispensary,
    strain,
    nullResults,
    getSearchResults,
    searchCategory,
  } = useSearch();

  const { publishEvent } = useEventTracker();
  const { asPath } = useRouter();
  const baseUrl = useDomain();

  useEffect(() => {
    const fetchSearchResults = async () =>
      await getSearchResults(SEARCH_INITIAL_TAKE, 0);

    fetchSearchResults();
  }, []);

  const resultingCategories = searchCategory
    ? [searchCategory]
    : categoryRelevanceOrder;

  const topCategory = resultingCategories[0];

  let topResult;
  if (topCategory === "brand") {
    topResult = brand[0]?.name;
  }
  if (topCategory === "dispensary") {
    topResult = dispensary[0]?.name;
  }
  if (topCategory === "strain") {
    topResult = strain[0]?.name;
  }

  const adTargeting = { category: topCategory, result: topResult };

  // Because of the complexitiy in MenuItemResults for loading,
  // we don't need to show the generic loading state for menu items
  const displayLoadingState =
    loading &&
    searchCategory !== "menuItem" &&
    categoryRelevanceOrder[0] !== "menuItem";

  return (
    <>
      <Head>
        <meta property="og:url" content={`${baseUrl}${asPath}`} key="og:url" />
        <title>Search | Leafly</title>
        <meta name="robots" content="noindex,noarchive,nofollow" />
      </Head>

      <section className="container pt-lg">
        <h1 className="heading--m mb-xl mt-sm">{`Search results for "${searchTerm}"`}</h1>

        {loading ? (
          <SearchFiltersPlaceholder />
        ) : (
          <>
            {nullResults ? (
              <TrackImpression
                category={Category.searchResults}
                label={`${Label.nullResults}_${searchTerm}`}
                className="mb-lg md:mb-xxl"
                data-testid="null-content"
              >
                Oh no! Your search for <strong>“{searchTerm}”</strong> has come
                up empty! Try a new search.
              </TrackImpression>
            ) : (
              <SearchFilter sortedCategories={categoryRelevanceOrder} />
            )}
          </>
        )}
      </section>

      <section className="bg-leafly-white py-lg">
        <SearchAd tag="upper" customTargeting={adTargeting} />
      </section>

      <section className="bg-gradient-to-b from-leafly-white to-white">
        {displayLoadingState ? (
          <SearchResultsPlaceholder />
        ) : (
          resultingCategories.map((category: string) => (
            <div
              key={`results-section-${category}`}
              className="container mb-section"
              data-testid={`results-section-${category}`}
            >
              {{
                article: <ArticleResults />,
                brand: <BrandResults />,
                dispensary: <DispensaryResults />,
                menuItem: <MenuItemResults />,
                strain: <StrainResults />,
              }[category] || null}
            </div>
          ))
        )}
      </section>

      {!loading && nullResults && (
        <DealsCarousel className="container my-section" />
      )}

      {!loading && (
        <RecentlyViewedMenuItems
          className="my-section container"
          onCardImpression={(menuItem) => {
            publishEvent({
              action: Action.impression,
              category: Category.recentlyViewedMenuItems,
              dispensaryDistance: getRoundedDistance(
                menuItem.dispensary.distanceMi,
                domainCountryCode,
              ),
              dispensaryId: menuItem.dispensaryId,
            });
          }}
        />
      )}

      <SearchAd
        tag="lower"
        customTargeting={adTargeting}
        className="mb-section"
      />
    </>
  );
};

export const getServerSideProps = createGetServerSideProps();

export default SearchPage;
